/**
 * ProjectTable - Standardized table components with retro theme support
 *
 * Provides consistent styling for project list tables across Site DNA and Flux EV
 */

import React from 'react';
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    alpha,
} from '@mui/material';
import { useThemeMode } from '../theme-mode/useThemeMode.js';
import { vortxColors, vortxTypography } from '../core/theme.js';

/**
 * ProjectTableContainer - Wrapper for project tables
 */
export function ProjectTableContainer({ children, ...props }) {
    const { mode } = useThemeMode();
    const isRetro = mode === 'retro';

    return (
        <TableContainer
            component={Paper}
            sx={isRetro ? {
                bgcolor: 'transparent',
                boxShadow: 'none',
            } : {
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
            }}
            {...props}
        >
            <Table>
                {children}
            </Table>
        </TableContainer>
    );
}

/**
 * ProjectTableHead - Standardized table header with retro theme support
 */
export function ProjectTableHead({ children, ...props }) {
    const { mode } = useThemeMode();
    const isRetro = mode === 'retro';

    return (
        <TableHead {...props}>
            <TableRow sx={isRetro ? {
                // Retro mode: black at 30% opacity (matches RetroTable standard)
                bgcolor: 'rgba(0, 0, 0, 0.3)',
                borderBottom: '1px solid rgba(178, 140, 80, 0.4)',
            } : {
                // Light mode: warmStone at 50% opacity
                bgcolor: alpha(vortxColors.warmStone, 0.5),
            }}>
                {children}
            </TableRow>
        </TableHead>
    );
}

/**
 * ProjectTableHeaderCell - Standardized header cell styling
 */
export function ProjectTableHeaderCell({ children, align = 'left', sx = {}, ...props }) {
    const { mode } = useThemeMode();
    const isRetro = mode === 'retro';

    return (
        <TableCell
            align={align}
            sx={{
                color: isRetro ? vortxColors.gold : 'text.primary',
                fontWeight: isRetro ? 600 : 700,
                fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                fontSize: isRetro ? '0.75rem' : '0.875rem',
                textTransform: isRetro ? 'uppercase' : 'none',
                letterSpacing: isRetro ? '0.1em' : 'normal',
                borderBottom: isRetro ? '1px solid rgba(178, 140, 80, 0.4)' : `2px solid ${vortxColors.gold}`,
                ...sx,
            }}
            {...props}
        >
            {children}
        </TableCell>
    );
}

/**
 * ProjectTableBody - Standardized table body
 */
export function ProjectTableBody({ children, className = '', ...props }) {
    return (
        <TableBody className={`vx-stagger ${className}`} {...props}>
            {children}
        </TableBody>
    );
}
